import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ExternalLink, KeyRound, User2 } from 'lucide-react';
import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { getPerfil, type PerfilData } from '@/lib/perfil';

function StepHeader({ index, title }: { index: number; title: string }) {
  return (
    <h2 className="text-heading-md text-fg">
      <span className="mr-2 inline-grid h-7 w-7 place-items-center border border-border bg-accent-100 font-mono text-caption font-semibold text-accent-800">
        {index}
      </span>
      {title.replace(/^\d+\.\s*/, '')}
    </h2>
  );
}

export function OnboardingPage() {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [perfil, setPerfil] = useState<PerfilData | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPerfil(apiClient)
      .then((data) => {
        if (!cancelled) setPerfil(data);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  return (
    <Blueprint
      as="section"
      data-testid="onboarding-page"
      aria-labelledby="onboarding-title"
      className="bg-bg p-8"
    >
      <h1 id="onboarding-title" className="font-display text-[34px] leading-none text-fg">
        {ptBR.onboarding.pageTitle}
      </h1>
      <p className="mt-2 text-body-md text-fg-muted">{ptBR.onboarding.intro}</p>

      <ol className="mt-8 flex flex-col gap-6">
        <li className="border border-border bg-fg/[0.03] p-5" data-testid="onboarding-step-1">
          <StepHeader index={1} title={ptBR.onboarding.step1Title} />
          <p className="mt-2 text-body-sm text-fg-muted">{ptBR.onboarding.step1Body}</p>
          <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[14rem_1fr]">
            <dt className="text-body-sm text-fg-muted">{ptBR.onboarding.step1EmailLabel}</dt>
            <dd className="flex items-center gap-2 text-body-md text-fg">
              <User2 className="h-4 w-4 text-fg-muted" aria-hidden="true" />
              <span data-testid="onboarding-email">
                {perfil?.email ?? ptBR.casos.placeholderEmDash}
              </span>
            </dd>
            <dt className="text-body-sm text-fg-muted">{ptBR.onboarding.step1EscritorioLabel}</dt>
            <dd
              className="flex flex-col gap-1 font-mono text-caption text-fg-muted"
              data-testid="onboarding-escritorio-id"
            >
              <span>{perfil?.escritorio_id ?? ptBR.casos.placeholderEmDash}</span>
              <span className="text-fg-muted">{ptBR.onboarding.step1EscritorioHint}</span>
            </dd>
          </dl>
          {loadFailed ? (
            <p
              className="mt-3 border border-warning-500 bg-warning-50 px-3 py-2 text-caption text-warning-700"
              role="status"
              data-testid="onboarding-context-error"
            >
              {ptBR.onboarding.contextLoadError}
            </p>
          ) : null}
        </li>

        <li className="border border-border bg-fg/[0.03] p-5" data-testid="onboarding-step-2">
          <StepHeader index={2} title={ptBR.onboarding.step2Title} />
          <p className="mt-2 text-body-sm text-fg-muted">{ptBR.onboarding.step2Body}</p>
          <Link
            to="/configuracoes/api-keys"
            data-testid="onboarding-step2-cta"
            className="mt-4 inline-flex items-center gap-2 border border-border px-3.5 py-1.5 text-body-sm text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.07] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <KeyRound className="h-4 w-4" aria-hidden="true" />
            {ptBR.onboarding.step2Cta}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </li>

        <li className="border border-border bg-fg/[0.03] p-5" data-testid="onboarding-step-3">
          <StepHeader index={3} title={ptBR.onboarding.step3Title} />
          <p className="mt-2 text-body-sm text-fg-muted">{ptBR.onboarding.step3Body}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              type="button"
              data-testid="onboarding-step3-primary"
              onClick={() => navigate('/casos/novo')}
            >
              {ptBR.onboarding.step3CtaPrimary}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              data-testid="onboarding-step3-secondary"
              onClick={() => navigate('/casos')}
            >
              {ptBR.onboarding.step3CtaSecondary}
            </Button>
          </div>
        </li>
      </ol>

      <p className="mt-8 text-center text-body-sm text-fg-muted">
        <button
          type="button"
          onClick={() => navigate('/casos')}
          data-testid="onboarding-skip"
          className="text-accent-700 underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {ptBR.onboarding.skipLink}
        </button>
      </p>
    </Blueprint>
  );
}
